import { useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, LabelList, CartesianGrid } from "recharts";
import { Filter, Info } from "lucide-react";
import { Section } from "../dc";
import { C, FUNNEL as F, actionable, won, open } from "./data";

const pct = (n, d) => `${Math.round((n / d) * 100)}%`;

// Waterfall: invisible base + visible step. Drops show what fell out at each stage.
const STEPS = [
  { k: "Leads in", v: F.in, base: 0, color: C.ink, note: "all leads in period" },
  { k: "Not a lead", v: F.notLead, base: F.in - F.notLead, color: C.grey, note: "spam, jobs, general" },
  { k: "Actionable", v: actionable, base: 0, color: C.teal, note: "base for conversion %" },
  { k: "Contacted", v: F.contacted, base: 0, color: C.teal, note: `${pct(F.contacted, actionable)} actioned` },
  { k: "Qualified", v: F.qualified, base: 0, color: C.teal, note: `${pct(F.qualified, actionable)} of actionable` },
  { k: "Won", v: won, base: 0, color: C.fill, note: `${F.converted} converted + ${F.existing} existing` },
  { k: "Lost", v: F.lost, base: won, color: C.neg, note: pct(F.lost, actionable) },
  { k: "No response", v: F.noResponse, base: won + F.lost, color: C.warn, note: pct(F.noResponse, actionable) },
  { k: "Still open", v: open, base: won + F.lost + F.noResponse, color: C.soft, note: "in pipeline now" },
].map((s) => ({ ...s, label: `${s.v} · ${pct(s.v, s.k === "Leads in" || s.k === "Not a lead" ? F.in : actionable)}` }));

const Tip = ({ active, payload }) => (active && payload?.length ? (
  <div className="card" style={{ padding: "8px 10px", fontSize: 12 }}>
    <b>{payload[0].payload.k}</b> · <span className="mono">{payload[0].payload.v}</span><div className="muted">{payload[0].payload.note}</div>
  </div>
) : null);

const FunnelHint = () => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <button className="btn btn-sm" onClick={() => setOpen(!open)} aria-label="How to use this chart" style={{ borderRadius: 999, width: 28, height: 28, padding: 0, justifyContent: "center" }} data-testid="lx-funnel-info">
        <Info size={14} />
      </button>
      {open && (
        <div className="card" style={{ position: "absolute", right: 0, top: 34, width: 260, padding: 12, fontSize: 12, zIndex: 5 }} data-testid="lx-funnel-hint">
          Click any bar to see the leads behind it in a list below. Click a name to open that lead in a new tab.
        </div>
      )}
    </div>
  );
};

export const FunnelWaterfall = ({ onPick }) => (
  <Section title="Lead funnel" sub="How leads move from first contact to first booking. % of actionable leads (leads in minus not-a-lead), same as today's conversion rate." icon={Filter} testid="lx-funnel" right={<FunnelHint />}>
    <div style={{ height: 300 }}>
      <ResponsiveContainer>
        <BarChart data={STEPS} margin={{ top: 24, right: 8, left: -12, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="#E6EEEF" />
          <XAxis dataKey="k" tick={{ fontSize: 11 }} interval={0} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip content={<Tip />} cursor={{ fill: "rgba(19,33,42,.04)" }} />
          <Bar dataKey="base" stackId="w" fill="transparent" isAnimationActive={false} />
          <Bar dataKey="v" stackId="w" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(d) => onPick(d.k, d.v)}>
            {STEPS.map((s) => <Cell key={s.k} fill={s.color} />)}
            <LabelList dataKey="label" position="top" style={{ fontSize: 11, fontFamily: "JetBrains Mono, monospace", fill: C.ink }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
    <div className="muted" style={{ fontSize: 12, marginTop: 6 }} data-testid="lx-funnel-summary">
      <b className="mono" style={{ color: C.ink }}>{F.in}</b> leads in → <b className="mono" style={{ color: C.ink }}>{F.contacted}</b> actioned ({pct(F.contacted, actionable)}) → <b className="mono" style={{ color: C.teal }}>{won}</b> won ({pct(won, actionable)}) · <b className="mono" style={{ color: C.neg }}>{F.lost}</b> lost · <b className="mono">{F.noResponse}</b> no response · <b className="mono">{open}</b> still open
    </div>
  </Section>
);
